/**
 * VERA-298 / VERA-299 / VERA-300 — certificate sharing.
 *
 * A "view" is one shareable rendition of a certificate: a link, an on/off switch,
 * a set of module visibility toggles, and an access rule. The design (Figma
 * `4541:788857`) uses ONE object for both stories — the public link is the
 * unnamed, non-deletable singleton view; custom ("dynamic") links are named,
 * N-per-certificate views. Same settings screen, same menu, same enforcement.
 *
 * Supersedes `TCertificateSettings` ({show_provenance, show_scan_history}), which
 * lived untyped in the `certificates.settings` JSONB column and only ever covered
 * the public link.
 */

export enum CertificateViewKind {
  /** The singleton public link. One per certificate, cannot be created or deleted. */
  Public = 'public',
  /** A user-created named link. Any number per certificate. */
  Custom = 'custom',
}

export enum CertificateViewAccess {
  /** Anyone holding the link can open it. */
  Anyone = 'anyone',
  /** Only the invited email addresses can open it. */
  Invited = 'invited',
}

/**
 * Which modules of the certificate this view exposes.
 *
 * Deliberately a JSONB blob rather than columns: the toggle list is expected to
 * grow, and adding a module must not require a migration. Read it through
 * `resolveCertificateViewVisibility` so a key added here defaults sanely on rows
 * written before it existed.
 */
export type TCertificateViewVisibility = {
  /**
   * YOUR identity as the current owner — the "Current owner" card (name, email,
   * owner-since). Distinct from `show_provenance`, which reveals OTHER people.
   */
  show_ownership: boolean;
  /**
   * The ownership HISTORY — previous owners' identities. A different privacy
   * decision from `show_ownership`: a reseller may want to prove the chain without
   * advertising that they currently hold the item, or exactly the reverse.
   */
  show_provenance: boolean;
  /**
   * The item's sale-price history. Fully independent of `show_provenance`.
   *
   * Price is a transaction fact, not anybody's identity, so it does not need the
   * ownership chain to stand on. With history hidden and prices shown, the timeline
   * renders anonymised — date and amount, no names — which is a genuinely useful
   * state: it establishes market value without exposing who held the item.
   *
   * (An earlier version made this depend on `show_provenance`. That was a UI artifact
   * of prices being drawn inside the owner cards, mistaken for a semantic rule.)
   */
  show_prices: boolean;
  /**
   * The scan history section. Its OWN category — deliberately independent of
   * provenance. Scans record the item's physical journey during YOUR ownership (who
   * tapped it, where); the ownership chain is a different fact about different people.
   * The legacy rule that tied them together came from the old two-boolean blob and had
   * no product reason behind it.
   */
  show_activity: boolean;
};

export interface ICertificateView {
  id: string;
  certificate_id: string;
  kind: CertificateViewKind;
  /** `null` for the public view — it is not user-nameable. */
  name: string | null;
  /** Opaque URL token. Public views get one too, so every link is uniform. */
  slug: string;
  is_enabled: boolean;
  access: CertificateViewAccess;
  visibility: TCertificateViewVisibility;
  created_at: Date;
  updated_at: Date;
  /**
   * Allowlist size. Only meaningful when `access` is `invited` — an `anyone` view is
   * reachable by everyone holding the link regardless of this number, so never render
   * it as "who can see this" without checking `access` first.
   */
  viewer_count?: number;
}

export interface ICertificateViewViewer {
  id: string;
  view_id: string;
  /** Stored lowercased; unique per view. */
  email: string;
  invited_at: Date;
  /** Set when the invitee FIRST opens the link while signed in. */
  accepted_at: Date | null;
  /**
   * Refreshed on EVERY successful open. `null` means never opened.
   *
   * Complete for invited-only views by construction: an anonymous request to one is
   * refused, so every successful view is attributable to a viewer row.
   */
  last_viewed_at: Date | null;
}

/**
 * What a view exposes when nothing has been configured.
 *
 * Matches the state drawn in the "Add custom view" frame (`3054:984346`):
 * ownership OFF (it is the sensitive one), provenance and activity ON.
 *
 * NOTE: this is NOT the backfill value for existing certificates' public views —
 * those preserve today's behaviour (ownership ON, provenance/activity carried
 * over from `settings`). See migration `0046_certificate_views.sql`.
 */
export const DEFAULT_CERTIFICATE_VIEW_VISIBILITY: TCertificateViewVisibility = {
  show_ownership: false,
  show_provenance: true,
  // Financials are opt-in even on a new link. Publishing what something sold for is a
  // deliberate act, never a default.
  show_prices: false,
  // Scans are opt-in for a stronger reason than prices: the scan log is a record of
  // where the OWNER'S OBJECT has been, so publishing it publishes their movements
  // ("Paris the 3rd, Milan the 7th"), and the rows also carry OTHER people's identities
  // — the owner consenting on behalf of strangers who scanned the tag.
  //
  // The owner can still turn it on. It should just never be something they published
  // without deciding to.
  show_activity: false,
};

/**
 * Fill in any module key missing from a stored blob, so rows written before a
 * toggle existed read as "off" rather than `undefined`. Use this at every read
 * site instead of touching `visibility` directly.
 */
export function resolveCertificateViewVisibility(
  stored: Partial<TCertificateViewVisibility> | null | undefined,
): TCertificateViewVisibility {
  return {
    show_ownership: stored?.show_ownership === true,
    show_provenance: stored?.show_provenance === true,
    // Added after the first views were written. Absent => false, so no existing view
    // starts publishing prices — which is why `visibility` is JSONB and not columns:
    // a new module needs no migration.
    show_prices: stored?.show_prices === true,
    show_activity: stored?.show_activity === true,
  };
}

/**
 * No cross-module rule remains — kept as the single call site so one can be added
 * without touching every controller.
 *
 * The legacy editor (`EditCertificate.tsx:39-41`) coupled scan activity to provenance,
 * and an earlier pass here coupled prices to it as well. Both were dropped: the four
 * modules answer different questions about different people, and the couplings made
 * legitimate combinations (prices without owners, scans without history) impossible
 * to express.
 */
export function isValidCertificateViewVisibility(
  visibility: TCertificateViewVisibility,
): boolean {
  // Every module is now independent: ownership (your identity), provenance (other
  // people's identities), prices (transaction facts), scans (the item's journey).
  // Nothing here constrains anything else.
  return true;
}
